// @ts-strict-ignore
import { ArticleConnectionPropertyValue } from '@article/constants/article-connection-property-values';
import { PropertyUtils } from '../util/property-utils';
import { ConnectionAreaVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Connection } from '@3d-scene/model3d/connection';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { InsertArticleHelper } from './insert-article.helper';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { ArticleUtils } from '../util/article-utils';
import { ArticleUsageUtils } from '../util/article-usage-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { MetaElementBehaviorPropertyDef } from '@project/constants/meta-element-behavior-property-def';
import { PropertyCreator } from '../util/property-creator';
import { GroupedCubesSearchRequirements } from '@project/model/vo/grouped-cubes-search-requirements';
import { PropertyVO } from '@project/components/article/model/vo/property.vo';
import { ConnectionCubeSearchRequirements } from '../components/connections/connection-creation/search/connection-cube-search-requirements';
import { SuitableConnectionsVO } from '@project/components/article/model/vo/suitable-connections.vo';
import { NumberUtils } from '@shared/util/number-utils';
import { ConnectionCubeSearchVector } from '../components/connections/connection-creation/search/connection-cube-search-vector';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { UndoAttachArticlesOperation } from '@shared/undo/operation/undo-attach-articles-operation';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { ArrayUtils } from '@shared/util/array-utils';
import { OverlayService } from '@shared/overlay/overlay.service';
import { OverlayBackground } from '@shared/overlay/overlay.type';
import { Dictionary } from 'typescript-collections';
import { ToastrService } from 'ngx-toastr';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ArticleConnectionPropertyDef } from '../constants/article-connection-property-def';
import { Injectable } from '@angular/core';
import { ArticleCreationManagerFactory } from '@project/components/article/manager/article-creation.manager.factory';
import { ArticleCreationManager } from '@project/components/article/manager/article-creation.manager';
import { ArticleInfoConnectionSetVO } from '@project/components/article/model/vo/accessory-search-result.vo';
import { ArticleManager } from '../manager/article.manager';
import { ArticleVO } from '../model/vo/article.vo';
import { map } from 'rxjs/operators';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { MetaElementUsageUtils } from '@project/util/meta-element-usage-utils';
import { AccessoryService } from '../service/accessory.service';
import { ArticleTypeConstants } from '../constants/article-type-constants';

interface BridgeArticlesGroup {
  articleContainers: ArticleContainer[];
  articlesBetweenCount: number;
  connectionsCount: number;
}

interface ConnectionCubeVectorGroup {
  min: number;
  max: number;
  reverse: boolean;
  articleContainers: ArticleContainer[];
}

@Injectable({ providedIn: 'root' })
export class BridgesConnectorHelper {
  private readonly MIN_ARTICLES_BETWEEN_BRIDGE_TERMINALS_COUNT: number = 0;
  private readonly MAX_ARTICLES_BETWEEN_BRIDGE_TERMINALS_COUNT: number = 1;

  private isPENBridges: boolean;

  constructor(
    private mountingRailHelper: MountingRailHelper,
    private selectionModelService: SelectionModelService,
    private undoRedoHelper: UndoRedoHelper,
    private overlayService: OverlayService,
    private toastrService: ToastrService,
    private localeResourceManager: LocaleResourceManager,
    private articleCreationManagerFactory: ArticleCreationManagerFactory,
    private undoRedoUtils: UndoRedoUtils,
    private containerService: ContainerService,
    private articleContainerUtils: ArticleContainerUtils,
    private propertyCreator: PropertyCreator,
    private articleManager: ArticleManager,
    private accessoryService: AccessoryService
  ) {}

  public async connectBridges(excludedGUIDs: string[] = [], isPENBridges: boolean = false, createUndoOperation: boolean = true): Promise<void> {
    this.isPENBridges = isPENBridges;
    const selectedContainers: ArticleContainer[] = this.selectionModelService.getArticleContainers();
    const containersToConnect: ArticleContainer[] = selectedContainers.length
      ? selectedContainers
      : this.containerService.enumerateArticleContainersInGlobalExcept(excludedGUIDs);
    await this.connectBridgesToContainers(containersToConnect, createUndoOperation);
  }

  private async connectBridgesToContainers(articleContainers: ArticleContainer[], createUndoOperation: boolean): Promise<void> {
    this.overlayService.requestOverlayShow(OverlayBackground.color);
    articleContainers = this.sortArticleContainers(articleContainers);
    articleContainers = this.filterArticleContainers(articleContainers);
    const groups: BridgeArticlesGroup[] = this.isPENBridges
      ? this.groupArticleContainersToConnectPENBridges(articleContainers)
      : this.groupArticleContainersToConnect(articleContainers);
    await this.connectAccessoryToArticlesGroup(groups, [], createUndoOperation);
  }

  private sortArticleContainers(articleContainers: ArticleContainer[]): ArticleContainer[] {
    const insertHelper: InsertArticleHelper = this.mountingRailHelper.getInsertArticleHelper();
    return insertHelper.sortArticles(articleContainers);
  }

  private groupArticleContainersToConnectPENBridges(articleContainers: ArticleContainer[]): BridgeArticlesGroup[] {
    const articleContainerGroups: BridgeArticlesGroup[] = [];
    const peConnection: Connection = this.findPENConnection(articleContainers, MetaElementBehaviorPropertyDef.IS_PE_JUMPER_CONNECTION);
    const nConnection: Connection = this.findPENConnection(articleContainers, MetaElementBehaviorPropertyDef.IS_N_POTENTIAL_JUMPER_CONNECTION);
    if (peConnection && nConnection) {
      const groupOfArticles: BridgeArticlesGroup = {
        articleContainers: [peConnection.articleContainer, nConnection.articleContainer],
        articlesBetweenCount: 0,
        connectionsCount: 1,
      };
      articleContainerGroups.push(groupOfArticles);
    }
    return articleContainerGroups;
  }

  private findPENConnection(articleContainers: ArticleContainer[], behaviorType: string): Connection {
    for (const articleContainer of articleContainers) {
      const potentialConnections: Connection[] = this.getPotentialConnections([articleContainer], behaviorType);
      if (ArrayUtils.isNotEmpty(potentialConnections)) {
        return potentialConnections[0];
      }
    }
    return null;
  }

  private groupArticleContainersToConnect(articleContainers: ArticleContainer[]): BridgeArticlesGroup[] {
    const articleContainerGroups: BridgeArticlesGroup[] = [];
    let articlesBetween: number = 0;
    let groupOfArticles: BridgeArticlesGroup = {
      articleContainers: [],
      articlesBetweenCount: 0,
      connectionsCount: 0,
    };
    const cleanGroup = () => {
      if (groupOfArticles && groupOfArticles.articleContainers.length) {
        if (
          groupOfArticles.articlesBetweenCount >= this.MIN_ARTICLES_BETWEEN_BRIDGE_TERMINALS_COUNT &&
          groupOfArticles.articlesBetweenCount <= this.MAX_ARTICLES_BETWEEN_BRIDGE_TERMINALS_COUNT
        ) {
          articleContainerGroups.push(groupOfArticles);
        }
        groupOfArticles = {
          articleContainers: [],
          articlesBetweenCount: 0,
          connectionsCount: 0,
        };
      }
    };
    for (let i = 0; i < articleContainers.length; i++) {
      const articleContainer: ArticleContainer = articleContainers[i];
      const nPotentialJumperConnectionsCount: number = this.getNPotentialJumperConnections([articleContainer]).length;
      if (nPotentialJumperConnectionsCount > 0) {
        if (groupOfArticles.connectionsCount > 0 && groupOfArticles.connectionsCount !== nPotentialJumperConnectionsCount) {
          cleanGroup();
          articlesBetween = 0;
        }
        if (groupOfArticles.articleContainers.length > 1 && groupOfArticles.articlesBetweenCount !== articlesBetween) {
          const lastContainerOfPrevGroup: ArticleContainer = groupOfArticles.articleContainers[groupOfArticles.articleContainers.length - 1];
          cleanGroup();
          groupOfArticles.articleContainers.push(lastContainerOfPrevGroup);
        }
        groupOfArticles.articleContainers.push(articleContainer);
        groupOfArticles.articlesBetweenCount = articlesBetween;
        groupOfArticles.connectionsCount = nPotentialJumperConnectionsCount;
        articlesBetween = 0;
      } else if (this.articleContainerUtils.isBusbarCarrierArticle(articleContainer) || ArticleUtils.isEndStopArticle(articleContainer.article)) {
        cleanGroup();
        articlesBetween = 0;
      } else if (groupOfArticles.articleContainers.length && !ArticleUtils.isEndPlateArticle(articleContainer.article)) {
        articlesBetween++;
      } else if (ArticleUtils.isEndPlateArticle(articleContainer.article)) {
        const previousArticleContainer: ArticleContainer = articleContainers[i - 1];
        if (previousArticleContainer && ArticleUsageUtils.isLastTerminalInCircuit(previousArticleContainer.articleUsage)) {
          cleanGroup();
          articlesBetween = 0;
        }
      }
    }
    cleanGroup();
    return articleContainerGroups;
  }

  private filterArticleContainers(articleContainers: ArticleContainer[]): ArticleContainer[] {
    const articleContainersUnderBusbars: ArticleContainer[] = this.getArticlesUnderBusbarCarriers(articleContainers);
    return articleContainersUnderBusbars.length
      ? articleContainers.filter((a) => articleContainersUnderBusbars.indexOf(a) === -1)
      : articleContainers;
  }

  private getArticlesUnderBusbarCarriers(articleContainers: ArticleContainer[]): ArticleContainer[] {
    let articleContainersUnderBusbars: ArticleContainer[] = [];
    const busbarCarrierArticlesGroups = this.getBusbarCarriersGroups(articleContainers);
    if (busbarCarrierArticlesGroups.length) {
      const insertHelper: InsertArticleHelper = this.mountingRailHelper.getInsertArticleHelper();
      busbarCarrierArticlesGroups.forEach((group) => {
        const firstCarrier: ArticleContainer = group[0];
        const lastCarrier: ArticleContainer = group[group.length - 1];
        const articlesUnderGroup: ArticleContainer[] = insertHelper.getConnectedArticlesBetweenContainers(
          firstCarrier,
          lastCarrier,
          [firstCarrier, lastCarrier].map((a) => a.articleGUID)
        );
        articleContainersUnderBusbars.push(...articlesUnderGroup);
      });
      articleContainersUnderBusbars = ArrayUtils.distinct(articleContainersUnderBusbars);
    }
    return articleContainersUnderBusbars;
  }

  private getBusbarCarriersGroups(articleContainers: ArticleContainer[]): ArticleContainer[][] {
    const busbarGuidToBusbarCarrierArticles: Dictionary<string, ArticleContainer[]> = new Dictionary<string, ArticleContainer[]>();
    for (const articleContainer of articleContainers) {
      if (this.articleContainerUtils.isBusbarCarrierArticle(articleContainer)) {
        const connectedBusbars: ArticleContainer[] = this.getConnectedBusbars(articleContainer);
        if (connectedBusbars.length) {
          connectedBusbars.forEach((b) => {
            if (busbarGuidToBusbarCarrierArticles.containsKey(b.articleGUID)) {
              busbarGuidToBusbarCarrierArticles.setValue(
                b.articleGUID,
                busbarGuidToBusbarCarrierArticles.getValue(b.articleGUID).concat([articleContainer])
              );
            } else {
              busbarGuidToBusbarCarrierArticles.setValue(b.articleGUID, [articleContainer]);
            }
          });
        }
      }
    }
    const busbarCarrierArticlesGroups: ArticleContainer[][] = busbarGuidToBusbarCarrierArticles.values().filter((v) => v.length > 1);
    return busbarCarrierArticlesGroups;
  }

  private createGroupedCubesSearchRequirements(bridgeArticlesGroup: BridgeArticlesGroup): GroupedCubesSearchRequirements {
    const connections: Connection[] = [];
    if (this.isPENBridges) {
      connections.push(this.findPENConnection(bridgeArticlesGroup.articleContainers, MetaElementBehaviorPropertyDef.IS_PE_JUMPER_CONNECTION));
      connections.push(
        this.findPENConnection(bridgeArticlesGroup.articleContainers, MetaElementBehaviorPropertyDef.IS_N_POTENTIAL_JUMPER_CONNECTION)
      );
    } else {
      bridgeArticlesGroup.articleContainers.forEach((a) => connections.push(...this.getNPotentialJumperConnections([a])));
    }
    const groupedSearchRequirements: GroupedCubesSearchRequirements = new GroupedCubesSearchRequirements(
      this.propertyCreator.createCubesSearchRequirementsByDefault(connections)
    );
    groupedSearchRequirements.searchRequirements.forEach((r) => this.addPotentialConnectionPropertyToRequirements(r));
    return groupedSearchRequirements;
  }

  private addPotentialConnectionPropertyToRequirements(connectionCubeSearchRequirements: ConnectionCubeSearchRequirements): void {
    const connectionMetaElement: MetaElementVO = connectionCubeSearchRequirements.getConnectionCube().metaElementVO;
    if (connectionMetaElement) {
      const nProperty: PropertyVO = PropertyUtils.getPropertyByName(
        connectionMetaElement.behaviorProperties,
        MetaElementBehaviorPropertyDef.IS_N_POTENTIAL_JUMPER_CONNECTION
      );
      if (nProperty) {
        connectionCubeSearchRequirements.connectionRequirements.push(nProperty);
      }
      if (this.isPENBridges) {
        const peProperty: PropertyVO = PropertyUtils.getPropertyByName(
          connectionMetaElement.behaviorProperties,
          MetaElementBehaviorPropertyDef.IS_PE_JUMPER_CONNECTION
        );
        if (peProperty) {
          connectionCubeSearchRequirements.connectionRequirements.push(peProperty);
        }
      }
    }
  }

  private getNPotentialJumperConnections(articleContainers: ArticleContainer[]): Connection[] {
    return this.getPotentialConnections(articleContainers, MetaElementBehaviorPropertyDef.IS_N_POTENTIAL_JUMPER_CONNECTION);
  }

  private getPotentialConnections(articleContainers: ArticleContainer[], behaviorType: string): Connection[] {
    const acceptingConnections: Connection[] = ConnectionInfoUtils.getAcceptingConnections(articleContainers);
    return acceptingConnections.filter(
      (c) =>
        PropertyUtils.getBooleanPropertyValue(c.metaElementVO.behaviorProperties, behaviorType) &&
        PropertyUtils.getStringPropertyValue((c.metaElementVO as ConnectionAreaVO).connectionRequirements, ArticleConnectionPropertyDef.TYPE) ===
        ArticleConnectionPropertyValue.BRIDGE_SHAFT_CONNECTION_TYPE
    );
  }

  private getConnectedBusbars(articleContainer: ArticleContainer): ArticleContainer[] {
    const connectedArticles: ArticleContainer[] = ArticleUsageUtils.getAcceptingAreaIds(articleContainer.articleUsage)
      .map((id) => this.containerService.findArticleContainerInGlobal(id))
      .filter((a) => !!a);
    return connectedArticles.filter((a) => ArticleUtils.isBusbarArticle(a.article));
  }

  private async connectAccessoryToArticlesGroup(
    groups: BridgeArticlesGroup[],
    createdContainers: ArticleContainer[] = [],
    createUndo: boolean
  ): Promise<void> {
    if (!groups.length) {
      await this.bridgesConnectFinished(
        createdContainers.filter((c) => !!c),
        createUndo
      );
      return;
    }

    const articleCreationManager = this.articleCreationManagerFactory.getNewInstance();
    articleCreationManager.showFaultMessage = false;
    articleCreationManager.createUndoOperation = false;
    articleCreationManager.detachArticleUsageIds = null;
    articleCreationManager.replaceArticleUsageIds = [];

    const articlesGroup: BridgeArticlesGroup = groups.shift();
    if (!articlesGroup) {
      return this.connectAccessoryToArticlesGroup(groups, createdContainers, createUndo);
    }
    const groupedCubesSearchRequirements = [this.createGroupedCubesSearchRequirements(articlesGroup)];
    const searchQueryBuilder = this.accessoryService.createSearchQueryBuilderForGroup(groupedCubesSearchRequirements, [ArticleTypeConstants.TYPE_BRIDGE]);
    const connectionCubeVectorGroups: ConnectionCubeVectorGroup[] = this.groupConnectionCubeVectors(
      searchQueryBuilder.connectionCubeVectors,
      articlesGroup.connectionsCount
    );
    if (connectionCubeVectorGroups.length > 1) {
      groups.unshift(
        ...connectionCubeVectorGroups.map((g) => ({
          articleContainers: g.articleContainers,
          articlesBetweenCount: articlesGroup.articlesBetweenCount,
          connectionsCount: articlesGroup.connectionsCount,
        }))
      );
      articleCreationManager.clean();
      return this.connectAccessoryToArticlesGroup(groups, createdContainers, createUndo);
    } else {
      this.revertVectorsOrder(searchQueryBuilder.connectionCubeVectors, connectionCubeVectorGroups);
      this.accessoryService.searchAccessoriesForBuilderWithoutPrintLabeling(searchQueryBuilder)
        .subscribe(accessorySearchResult => this.accessoryListLoaded(accessorySearchResult.vo.articleInfoConnectionSet, articleCreationManager, groups, createdContainers, createUndo, accessorySearchResult.connectionCubeVectors));
    }
  }

  private isVectorsReverseNeeded(group: BridgeArticlesGroup): boolean {
    const firstContainer: ArticleContainer = group.articleContainers[0];
    if (firstContainer) {
      const nPotentialJumperConnections: Connection[] = this.getNPotentialJumperConnections([firstContainer]);
      if (nPotentialJumperConnections.length > 1) {
        nPotentialJumperConnections.sort((a, b) => NumberUtils.numericCompare(b.globalRectangle.point1.z, a.globalRectangle.point1.z));
        const farestConnection: Connection = nPotentialJumperConnections[0];
        return !MetaElementUsageUtils.isFreeConnectionUsage(farestConnection.metaElementUsage, 1);
      }
    }
    return false;
  }

  private revertVectorsOrder(connectionCubeVectors: ConnectionCubeSearchVector[], connectionCubeVectorGroups: ConnectionCubeVectorGroup[]): void {
    if (!connectionCubeVectorGroups.some((g) => g.reverse)) {
      return;
    }
    const zPart: ConnectionCubeSearchVector[] = connectionCubeVectors.filter((v) => v.directionAxis === 'z');
    const xPart: ConnectionCubeSearchVector[] = connectionCubeVectors.filter((v) => v.directionAxis === 'x');
    const resultXPart: ConnectionCubeSearchVector[] = [];
    const otherPart: ConnectionCubeSearchVector[] = connectionCubeVectors.filter((v) => v.directionAxis !== 'z' && v.directionAxis !== 'x');

    for (const group of connectionCubeVectorGroups) {
      zPart.forEach((vector) => {
        const indexes: number[] = vector.sortedCubesSearchRequirements.map((i) => i.globalRequirementIndex);
        const min: number = Math.min(...indexes);
        const max: number = Math.max(...indexes);
        if (group.reverse && group.min <= min && group.max >= max) {
          vector.sortedCubesSearchRequirements = vector.sortedCubesSearchRequirements.reverse();
          vector.sortedCubesSearchRequirements.forEach((r, i) => (r.globalRequirementIndex = min + i));
        }
      });
      const filteredXPart: ConnectionCubeSearchVector[] = xPart.filter((v) => {
        const indexes: number[] = v.sortedCubesSearchRequirements.map((i) => i.globalRequirementIndex);
        const min: number = Math.min(...indexes);
        const max: number = Math.max(...indexes);
        return group.min <= min && group.max >= max;
      });
      if (group.reverse) {
        filteredXPart.reverse();
      }
      resultXPart.push(...filteredXPart);
    }
    const newArr: ConnectionCubeSearchVector[] = [...zPart, ...resultXPart, ...otherPart];
    newArr.forEach((v, i) => (v.id = i));
    connectionCubeVectors.splice(0, connectionCubeVectors.length);
    connectionCubeVectors.push(...newArr);
  }

  protected groupConnectionCubeVectors(
    connectionCubeVectors: ConnectionCubeSearchVector[],
    connectionsPerArticleCount: number
  ): ConnectionCubeVectorGroup[] {
    const groups: ConnectionCubeVectorGroup[] = [];
    const xVectors: ConnectionCubeSearchVector[] = connectionCubeVectors.filter((v) => v.directionAxis === 'x');
    for (let i = 0; i < xVectors.length; i += connectionsPerArticleCount) {
      let min: number = Infinity,
        max: number = -Infinity,
        reverse: boolean = false;
      const indexes: number[] = [];
      const cubesSearchRequirements: ConnectionCubeSearchRequirements[] = [];
      for (let j = 0; j < connectionsPerArticleCount; j++) {
        xVectors[i + j].sortedCubesSearchRequirements.forEach((r) => {
          indexes.push(r.globalRequirementIndex);
          cubesSearchRequirements.push(r);
        });
      }

      min = Math.min(...indexes);
      const indexesArr: number[][] = [];
      let arrIndex: number = 0,
        index: number = 0;
      indexes.sort(NumberUtils.numericCompare).forEach((i) => {
        if (i !== min + index) {
          min = i;
          index = 0;
          arrIndex++;
        }
        index++;
        if (!indexesArr[arrIndex]) {
          indexesArr[arrIndex] = [];
        }
        indexesArr[arrIndex].push(i);
      });

      indexesArr.forEach((arr) => {
        max = Math.max(...arr);
        min = Math.min(...arr);
        const filtered: ConnectionCubeSearchRequirements[] = cubesSearchRequirements.filter((r) => arr.indexOf(r.globalRequirementIndex) > -1);
        const minReq: ConnectionCubeSearchRequirements = filtered.find((r) => r.globalRequirementIndex === min);
        reverse = this.isVectorsReverseNeeded({ articleContainers: [minReq.getConnectionCube().articleContainer] } as BridgeArticlesGroup);
        const articleContainers: ArticleContainer[] = ArrayUtils.distinct(filtered.map((r) => r.getConnectionCube().articleContainer));
        groups.push({ min, max, reverse, articleContainers });
      });
    }
    groups.sort((a, b) => NumberUtils.numericCompare(a.min, b.min));
    return groups;
  }

  private async accessoryListLoaded(
    accessoryList: ArticleInfoConnectionSetVO[],
    articleCreationManager: ArticleCreationManager,
    processedGroups: BridgeArticlesGroup[],
    createdContainers: ArticleContainer[],
    createUndo: boolean,
    connectionCubeVectors: ConnectionCubeSearchVector[]
  ): Promise<void> {
    const accToConnect: ArticleInfoConnectionSetVO = accessoryList.length ? accessoryList.sort(this.sortAccessoriesFn)[0] : null;
    if (accToConnect) {
      await this.connectArticleBySuitableConnections(createdContainers, articleCreationManager, processedGroups, accToConnect, connectionCubeVectors, createUndo);
    } else {
      await this.onArticlesAdded(createdContainers, articleCreationManager, processedGroups, createUndo);
    }
  }

  private onArticlesAdded(
    createdContainers: ArticleContainer[],
    articleCreationManager: ArticleCreationManager,
    processedGroups: BridgeArticlesGroup[],
    createUndo: boolean
  ): Promise<void> {
    createdContainers.push(...articleCreationManager.getCreatedArticleContainers());
    articleCreationManager.clean();
    return this.connectAccessoryToArticlesGroup(processedGroups, createdContainers, createUndo);
  }

  private sortAccessoriesFn(a: ArticleInfoConnectionSetVO, b: ArticleInfoConnectionSetVO): number {
    return NumberUtils.numericCompare(b.connectionSets.length, a.connectionSets.length);
  }

  private async connectArticleBySuitableConnections(
    createdContainers: ArticleContainer[],
    articleCreationManager: ArticleCreationManager,
    processedGroups: BridgeArticlesGroup[],
    articleInfoConnection: ArticleInfoConnectionSetVO,
    connectionCubeVectors: ConnectionCubeSearchVector[],
    createUndo: boolean
  ): Promise<void> {
    if (ArticleUtils.isArticleOutdated(articleInfoConnection.articleWithTechnicalProperties)) {
      return;
    }
    const suitableConnections = await this.articleManager.loadFullArticle(articleInfoConnection.articleWithTechnicalProperties.articleId).pipe(
      map((article: ArticleVO) => {
        const suitableConnections: SuitableConnectionsVO = new SuitableConnectionsVO();
        suitableConnections.connectionSets = articleInfoConnection.connectionSets;
        suitableConnections.article = article;
        return suitableConnections;
      })).toPromise();
    await articleCreationManager.createArticlesBySuitableConnections(suitableConnections, connectionCubeVectors);
    await this.onArticlesAdded(createdContainers, articleCreationManager, processedGroups, createUndo)
  }

  private async bridgesConnectFinished(createdContainers: ArticleContainer[] = [], createUndo: boolean): Promise<void> {
    if (createUndo) {
      await this.createUndoOperation(createdContainers);
      this.displayToasterMessage(createdContainers.length);
    }
    this.overlayService.requestOverlayHide(OverlayBackground.color);
  }

  private async createUndoOperation(createdContainers: ArticleContainer[] = []): Promise<void> {
    if (ArrayUtils.isNotEmpty(createdContainers)) {
      const undoOperation: UndoAttachArticlesOperation = this.undoRedoUtils.createUndoAttachOperationByArticleContainers(createdContainers);
      await this.undoRedoHelper.pushUndoOperation(undoOperation);
    }
  }

  private displayToasterMessage(createdContainersCount: number): void {
    const toasterMsg: string = !createdContainersCount
      ? this.localeResourceManager.getValueByKey('iekAssistant.toaster.message.no_jumper_inserted')
      : this.localeResourceManager.getValueByKey('iekAssistant.toaster.message.jumpers_inserted', { 0: createdContainersCount });
    this.toastrService.show(toasterMsg, '');
  }
}
